import { Vec2 } from '@lakaran/geometry';
import { evaluateNode, refOptions, type NodeGraph, type RefKind } from '@lakaran/nodes';
import type { CadEditor } from '../src/editor/editor';

/**
 * Viewport picker for a node's reference fields.
 *
 * The node card can list every origin/vertex/centre/midline in a dropdown, but picking the point
 * on the drawing is what people actually reach for: arm the picker from the card, move the mouse,
 * the nearest candidate highlights (green ring + its name), click to bind. Escape or a right-click
 * aborts. Nothing else in the viewport reacts while the picker is armed.
 */

export interface RefPickRequest {
  nodeId: string;
  refId: string;
  /** Human label of the field, e.g. "Move to". */
  label: string;
  kinds?: readonly RefKind[];
}

export interface RefPickerOptions {
  graph: () => NodeGraph;
  onPick: (request: RefPickRequest, value: string) => void;
  onCancel?: () => void;
}

interface Candidate {
  value: string;
  label: string;
  point: Vec2;
  kind: RefKind;
  /** The object's own outline, so a click anywhere on it picks the object. */
  paths?: Array<{ points: Vec2[]; closed: boolean }>;
}

/** Screen-pixel radius within which a candidate counts as hovered. */
const HOVER_PX = 14;
const DOT_LIMIT = 800;

/** Distance from a world point to the nearest of some flattened outlines. */
function distanceToPaths(
  at: Vec2,
  paths: ReadonlyArray<{ points: Vec2[]; closed: boolean }>,
): number {
  let best = Infinity;
  for (const path of paths) {
    const points = path.points;
    const count = points.length;
    if (count < 2) continue;
    for (let i = 1; i < count; i++) {
      best = Math.min(best, distanceToSegment(at, points[i - 1]!, points[i]!));
    }
    if (path.closed) best = Math.min(best, distanceToSegment(at, points[count - 1]!, points[0]!));
  }
  return best;
}

function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const ab = b.sub(a);
  const lengthSquared = ab.x * ab.x + ab.y * ab.y;
  if (lengthSquared < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * ab.x + (p.y - a.y) * ab.y) / lengthSquared));
  return Math.hypot(p.x - (a.x + ab.x * t), p.y - (a.y + ab.y * t));
}

export interface RefPicker {
  start(request: RefPickRequest): void;
  cancel(): void;
  get active(): boolean;
  dispose(): void;
}

export function createRefPicker(
  editor: CadEditor,
  host: HTMLElement,
  options: RefPickerOptions,
): RefPicker {
  const layer = document.createElement('div');
  layer.className = 'ref-pick-layer';
  layer.hidden = true;

  const hint = document.createElement('div');
  hint.className = 'ref-pick-hint';
  const dots = document.createElement('div');
  dots.className = 'ref-pick-dots';
  const label = document.createElement('div');
  label.className = 'ref-pick-label';
  label.hidden = true;
  layer.append(dots, label, hint);
  host.append(layer);

  const canvas = host.querySelector('canvas');
  let session: { request: RefPickRequest; candidates: Candidate[]; hovered: Candidate | null } | null =
    null;
  let unlistenView: (() => void) | null = null;

  const screenOf = (point: Vec2): Vec2 => editor.viewport.worldToScreen(point);

  const withinView = (screen: Vec2): boolean =>
    screen.x > -60 &&
    screen.y > -60 &&
    screen.x < editor.viewport.width + 60 &&
    screen.y < editor.viewport.height + 60;

  const render = (): void => {
    if (!session) return;
    dots.replaceChildren();
    let drawn = 0;
    for (const candidate of session.candidates) {
      if (drawn >= DOT_LIMIT) break;
      // An object pick is the whole primitive, highlighted the way a selection is — not a dot.
      if (candidate.paths) {
        if (candidate !== session.hovered) continue;
        for (const path of candidate.paths) {
          if (path.points.length < 2) continue;
          let svg = dots.querySelector('svg');
          if (!svg) {
            svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('class', 'ref-pick-shapes');
            dots.append(svg);
          }
          const shape = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
          shape.setAttribute(
            'points',
            path.points
              .map((point) => {
                const screen = screenOf(point);
                return `${screen.x.toFixed(1)},${screen.y.toFixed(1)}`;
              })
              .join(' '),
          );
          shape.setAttribute('class', 'ref-pick-shape');
          if (path.closed) shape.setAttribute('data-closed', '');
          svg.append(shape);
        }
        drawn += 1;
        continue;
      }
      const screen = screenOf(candidate.point);
      if (!withinView(screen)) continue;
      const dot = document.createElement('span');
      dot.className = 'ref-pick-dot';
      // Crossings read as crosses, so a pick is never a guess about which dot is which.
      if (candidate.kind === 'intersection') dot.classList.add('is-intersection');
      dot.title = candidate.label;
      dot.style.left = `${screen.x}px`;
      dot.style.top = `${screen.y}px`;
      if (candidate === session.hovered) dot.classList.add('is-hot');
      dots.append(dot);
      drawn += 1;
    }

    if (!session.hovered) {
      label.hidden = true;
      return;
    }
    const screen = screenOf(session.hovered.point);
    label.hidden = false;
    label.textContent = session.hovered.label;
    label.style.left = `${screen.x}px`;
    label.style.top = `${screen.y}px`;
    label.classList.toggle('is-left', screen.x > editor.viewport.width - 160);
  };

  /** `at` is already in world millimetres, as the callers pass it. */
  const nearest = (at: Vec2): Candidate | null => {
    if (!session) return null;
    const reach = HOVER_PX / Math.max(editor.viewport.scale, 1e-9);
    let best: { candidate: Candidate; distance: number } | null = null;
    for (const candidate of session.candidates) {
      // An object is picked anywhere on it; a point is picked near it.
      const distance = candidate.paths
        ? distanceToPaths(at, candidate.paths)
        : Math.hypot(candidate.point.x - at.x, candidate.point.y - at.y);
      if (distance > reach || (best && distance >= best.distance)) continue;
      best = { candidate, distance };
    }
    return best?.candidate ?? null;
  };

  const toLocal = (event: PointerEvent | MouseEvent): Vec2 => {
    const rect = canvas?.getBoundingClientRect();
    return Vec2.of(event.clientX - (rect?.left ?? 0), event.clientY - (rect?.top ?? 0));
  };

  const onMove = (event: PointerEvent): void => {
    if (!session) return;
    event.stopPropagation();
    const world = editor.viewport.screenToWorld(toLocal(event));
    const hovered = nearest(world);
    if (hovered === session.hovered) return;
    session.hovered = hovered;
    render();
  };

  const onDown = (event: PointerEvent): void => {
    if (!session) return;
    // The picker owns the pointer while it is armed: no tool, no selection band.
    event.stopPropagation();
    event.preventDefault();
    if (event.button !== 0 || !session.hovered) return;
    const picked = session.hovered;
    const request = session.request;
    finish();
    options.onPick(request, picked.value);
  };

  const onContext = (event: MouseEvent): void => {
    if (!session) return;
    event.preventDefault();
    event.stopPropagation();
    cancel();
  };

  const onKey = (event: KeyboardEvent): void => {
    if (!session) return;
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    cancel();
  };

  const onView = (): void => render();

  const finish = (): void => {
    session = null;
    layer.hidden = true;
    dots.replaceChildren();
    label.hidden = true;
    hint.textContent = '';
    unlistenView?.();
    unlistenView = null;
    canvas?.removeEventListener('pointermove', onMove, true);
    canvas?.removeEventListener('pointerdown', onDown, true);
    canvas?.removeEventListener('contextmenu', onContext, true);
    window.removeEventListener('keydown', onKey, true);
    canvas?.classList.remove('is-picking');
  };

  const cancel = (): void => {
    if (!session) return;
    finish();
    options.onCancel?.();
  };

  const start = (request: RefPickRequest): void => {
    finish();
    const graph = options.graph();
    const evaluate = (nodeId: string) => evaluateNode(graph, nodeId, editor.doc.currentLayerId);
    const candidates: Candidate[] = refOptions(graph, request.nodeId, evaluate, request.kinds).map(
      (option) => ({
        value: option.value,
        label: option.label,
        point: option.point,
        kind: option.kind,
        paths: option.paths,
      }),
    );

    if (candidates.length === 0) {
      editor.message(`PICK  Nothing to pick for ${request.label} — add a step first.`);
      return;
    }

    session = { request, candidates, hovered: null };
    layer.hidden = false;
    // An object pick lights whole primitives, so say so rather than talking about a ring.
    hint.textContent = candidates.some((candidate) => candidate.paths)
      ? `Pick ${request.label} — hover a shape, click it, Esc cancels`
      : `Pick ${request.label} — green ring marks the point, click to bind, Esc cancels`;
    canvas?.classList.add('is-picking');
    unlistenView = editor.events.on('view', onView);
    canvas?.addEventListener('pointermove', onMove, true);
    canvas?.addEventListener('pointerdown', onDown, true);
    canvas?.addEventListener('contextmenu', onContext, true);
    window.addEventListener('keydown', onKey, true);
    render();
    editor.message(`PICK  Choose ${request.label} in the drawing (Esc cancels).`);
  };

  return {
    start,
    cancel,
    get active() {
      return session !== null;
    },
    dispose: () => finish(),
  };
}
